import { inferColumnType } from './infer-column-type.js';
import type { ImportColumn, ImportRow, ImportTable } from './types.js';

function detectDelimiter(line: string): string {
  const counts: { d: string; n: number }[] = [
    { d: ',', n: 0 },
    { d: ';', n: 0 },
    { d: '\t', n: 0 },
  ];
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        i++;
        continue;
      }
      inQuotes = !inQuotes;
      continue;
    }
    if (!inQuotes) {
      for (const c of counts) {
        if (ch === c.d) {
          c.n++;
        }
      }
    }
  }
  counts.sort((a, b) => b.n - a.n);
  return counts[0]!.n > 0 ? counts[0]!.d : ',';
}

function parseCsvLine(line: string, delimiter: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cur += ch;
      }
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      continue;
    }
    if (ch === delimiter) {
      out.push(cur);
      cur = '';
      continue;
    }
    cur += ch;
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

function splitCsvRecords(text: string): string[] {
  const lines: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '"') {
      if (inQuotes && text[i + 1] === '"') {
        cur += '""';
        i++;
        continue;
      }
      inQuotes = !inQuotes;
      cur += ch;
      continue;
    }
    if (!inQuotes && (ch === '\n' || ch === '\r')) {
      if (ch === '\r' && text[i + 1] === '\n') {
        i++;
      }
      if (cur.length > 0 || lines.length > 0) {
        lines.push(cur);
        cur = '';
      }
      continue;
    }
    cur += ch;
  }
  if (cur.length > 0 || lines.length === 0) {
    lines.push(cur);
  }
  return lines.filter((l) => l.trim().length > 0);
}

export function parseCsvToImportTable(
  csvText: string,
  options?: { name?: string },
): ImportTable {
  const normalized = csvText.replace(/^\uFEFF/, '');
  const records = splitCsvRecords(normalized);
  if (records.length === 0) {
    return { name: options?.name ?? 'Import', columns: [], rows: [] };
  }

  const delimiter = detectDelimiter(records[0]!);
  const headerCells = parseCsvLine(records[0]!, delimiter);
  const headers = headerCells.map((h, i) => (h.trim() ? h.trim() : `Column ${i + 1}`));

  const columnValues: string[][] = headers.map(() => []);
  const rows: ImportRow[] = [];

  for (let r = 1; r < records.length; r++) {
    const cells = parseCsvLine(records[r]!, delimiter);
    const rowCells: Record<string, string> = {};
    let hasData = false;
    for (let c = 0; c < headers.length; c++) {
      const name = headers[c]!;
      const val = cells[c] ?? '';
      rowCells[name] = val;
      if (val.trim()) {
        hasData = true;
        columnValues[c]!.push(val);
      }
    }
    if (hasData) {
      rows.push({ cells: rowCells });
    }
  }

  const columns: ImportColumn[] = headers.map((sourceName, i) => {
    const samples = columnValues[i]!.slice(0, 20);
    return {
      sourceName,
      inferredType: inferColumnType(samples.length > 0 ? samples : ['']),
      sampleValues: samples,
    };
  });

  return {
    name: options?.name ?? 'Import',
    columns,
    rows,
  };
}
